import { Badge, Tooltip, TooltipContent, TooltipTrigger } from '@frontend-platform/ui-lib/ui';
import {
  CheckCircle2,
  Circle,
  CircleAlert,
  Clock4,
  Loader2,
  OctagonX,
  XCircle,
} from 'lucide-react';
import { isInProgressStatus } from '../lib/status';
import type { CalibrationRunStatus } from '../lib/types';

interface RunStatusBadgeProps {
  status: CalibrationRunStatus;
}

type StatusConfig = {
  label: string;
  icon: React.ReactNode;
  className: string;
  /** Hover copy on the badge, e.g. explaining why a run was cancelled. */
  tooltip?: string;
};

// Backend statuses funnel through several intermediate steps
// (SENT_FOR_PREPROCESSING, SENT_TO_ANALYTICS, ...) while a run is active —
// mirrors aistudio's RUN_STATUS_CONFIG, which maps all of
// `IN_PROGRESS_STATUSES` to the same "In Progress" entry rather than
// surfacing the raw status. SCHEDULED gets its own distinct badge there too.
// INITIATED has no entry in aistudio's config; it is given one here so a
// pre-run status reads as itself rather than as "In progress" — even though
// `isInProgressStatus` does count it as in-progress for slot/queue purposes
// (see the divergence note in lib/status).
function getConfig(status: CalibrationRunStatus): StatusConfig {
  if (status === 'SCHEDULED') {
    return {
      label: 'Scheduled',
      icon: <Clock4 className="h-3.5 w-3.5" />,
      className: 'bg-blue-50 text-blue-600 border-blue-200',
    };
  }
  if (status === 'INITIATED') {
    return {
      label: 'Initiated',
      icon: <Circle className="h-3.5 w-3.5" />,
      className: 'bg-blue-50 text-blue-500 border-blue-200',
    };
  }
  if (isInProgressStatus(status)) {
    return {
      label: 'In progress',
      icon: <Loader2 className="h-3.5 w-3.5 animate-spin" />,
      className: 'bg-blue-50 text-blue-600 border-blue-200',
    };
  }
  switch (status) {
    case 'COMPLETED':
      return {
        label: 'Completed',
        icon: <CheckCircle2 className="h-3.5 w-3.5" />,
        className: 'bg-green-50 text-green-600 border-green-200',
      };
    case 'FAILED':
      // Alert glyph, distinct from Canceled's X — both are red, so the icon is
      // what separates "something went wrong" from "someone stopped it".
      return {
        label: 'Failed',
        icon: <CircleAlert className="h-3.5 w-3.5" />,
        className: 'bg-red-50 text-red-500 border-red-200',
      };
    case 'STOPPED':
      return {
        label: 'Stopped',
        icon: <OctagonX className="h-3.5 w-3.5" />,
        className: 'bg-muted text-muted-foreground border-border',
      };
    case 'CANCELLED':
      // Reads as a terminal, unsuccessful outcome alongside Failed rather than
      // a neutral one — the "why" lives in the info icon beside the badge
      // (see RunStatusInfo in run-list-columns).
      return {
        label: 'Canceled',
        icon: <XCircle className="h-3.5 w-3.5" />,
        className: 'bg-red-50 text-red-500 border-red-200',
      };
    default:
      return {
        label: String(status ?? ''),
        icon: <Circle className="h-3.5 w-3.5" />,
        className: 'bg-muted text-muted-foreground border-border',
      };
  }
}

export function RunStatusBadge({ status }: RunStatusBadgeProps) {
  const { label, icon, className, tooltip } = getConfig(status);
  const badge = (
    <Badge variant="outline" className={`font-normal ${className}`}>
      {icon}
      {label}
    </Badge>
  );
  if (!tooltip) return badge;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{badge}</TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
}
